// Updated api.service.ts - Fixed for your backend
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  LoginRequest, 
  LoginResponse, 
  User, 
  Content, 
  PaginatedResponse, 
  ContentFilter, 
  CreateContentRequest,
  UploadResponse, 
  CreateUserRequest,
  UserFilter,
  DashboardStats,
  ContentType,
  ContentStatus,
  UserSummary
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1',
      timeout: 10000,
    });

    // Load token from localStorage
    this.token = localStorage.getItem('token');
    
    // Request interceptor
    this.api.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.removeToken();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('token', token);
  }

  removeToken(): void {
    this.token = null;
    localStorage.removeItem('token');
  }

  // Transform backend user data to frontend format
  private transformUser(backendUser: any): User {
    return {
      id: backendUser.id,
      name: backendUser.name,
      email: backendUser.email,
      role: backendUser.role,
      status: backendUser.status,
      employee_id: backendUser.employee_id,
      department: backendUser.department,
      designation: backendUser.designation,
      first_name: backendUser.first_name,
      last_name: backendUser.last_name,
      middle_name: backendUser.middle_name,
      phone: backendUser.phone,
      mobile: backendUser.mobile,
      address: backendUser.address,
      city: backendUser.city,
      state: backendUser.state,
      division: backendUser.division,
      circle: backendUser.circle,
      zone: backendUser.zone,
      last_login: backendUser.last_login ? new Date(backendUser.last_login) : undefined,
      login_attempts: backendUser.login_attempts,
      email_verified: backendUser.email_verified,
      profile_picture: backendUser.profile_picture,
      createdAt: new Date(backendUser.created_at),
      updatedAt: new Date(backendUser.updated_at),
    };
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response: AxiosResponse<LoginResponse> = await this.api.post('/auth/login', credentials);
    this.setToken(response.data.access_token);
    return response.data;
  }

  async getProfile(): Promise<User> {
    const response: AxiosResponse<any> = await this.api.get('/auth/profile');
    return this.transformUser(response.data);
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      this.removeToken();
    }
  }

  // Content endpoints - Updated for your backend structure
  async getContent(filters: ContentFilter = {}): Promise<PaginatedResponse<Content>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response: AxiosResponse<any> = await this.api.get(`/content?${params.toString()}`);
    
    // Transform backend response to match frontend expectations
    return {
      data: response.data.data || response.data.content || [],
      total: response.data.total || 0,
      page: response.data.currentPage || response.data.page || 1,
      totalPages: response.data.totalPages || 0,
      hasNext: response.data.hasNext,
      hasPrev: response.data.hasPrev,
    };
  }

  async getContentById(id: string): Promise<Content> {
    const response: AxiosResponse<Content> = await this.api.get(`/content/${id}`);
    return response.data;
  }

  async createContent(data: CreateContentRequest): Promise<Content> {
    const response: AxiosResponse<Content> = await this.api.post('/content', data);
    return response.data;
  }

  async updateContent(id: string, data: Partial<CreateContentRequest>): Promise<Content> {
    const response: AxiosResponse<Content> = await this.api.patch(`/content/${id}`, data);
    return response.data;
  }

  async deleteContent(id: string): Promise<void> {
    await this.api.delete(`/content/${id}`);
  }

  // User management - Updated for your backend
  async getUsers(filters: UserFilter = {}): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response: AxiosResponse<any> = await this.api.get(`/users?${params.toString()}`);
    
    // Transform backend response
    const users = (response.data.users || response.data.data || []).map(this.transformUser);
    
    return {
      data: users,
      total: response.data.total || 0,
      page: response.data.currentPage || response.data.page || 1,
      totalPages: response.data.totalPages || 0,
      hasNext: response.data.hasNext,
      hasPrev: response.data.hasPrev,
    };
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    const response: AxiosResponse<any> = await this.api.post('/users', userData);
    return this.transformUser(response.data);
  }

  async updateUser(id: string, userData: Partial<CreateUserRequest>): Promise<User> {
    const response: AxiosResponse<any> = await this.api.patch(`/users/${id}`, userData);
    return this.transformUser(response.data);
  }

  async deleteUser(id: string): Promise<void> {
    await this.api.delete(`/users/${id}`);
  }

  async getUserById(id: string): Promise<User> {
    const response: AxiosResponse<any> = await this.api.get(`/users/${id}`);
    return this.transformUser(response.data);
  }

  // Get user statistics from your backend
  async getUserStats(): Promise<UserSummary> {
    try {
      const response = await this.api.get('/users/statistics');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
      return {
        totalUsers: 0,
        activeUsers: 0,
        pendingUsers: 0,
        suspendedUsers: 0,
        inactiveUsers: 0,
        retiredUsers: 0,
        transferredUsers: 0,
        emailVerifiedUsers: 0,
        lockedUsers: 0,
        recentlyActiveUsers: 0,
      };
    }
  }

  // Get authors (users who can create content)
  async getAuthors(): Promise<User[]> {
    const response: AxiosResponse<any[]> = await this.api.get('/users/authors');
    return response.data.map(this.transformUser);
  }

  // Content stats
  async getContentStats(): Promise<DashboardStats> {
    try {
      const response = await this.api.get('/content/stats');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch content stats:', error);
      return {
        totalContent: 0,
        publishedContent: 0,
        draftContent: 0,
        archivedContent: 0,
        totalViews: 0,
      };
    }
  }

  // Dashboard stats
  async getDashboardStats(): Promise<DashboardStats> {
    return this.getContentStats();
  }

  // Search content
  async searchContent(query: string, filters: Partial<ContentFilter> = {}): Promise<PaginatedResponse<Content>> {
    const params = new URLSearchParams();
    params.append('search', query);
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response: AxiosResponse<any> = await this.api.get(`/content/search?${params.toString()}`);
    
    return {
      data: response.data.data || response.data.content || [],
      total: response.data.total || 0,
      page: response.data.currentPage || response.data.page || 1,
      totalPages: response.data.totalPages || 0,
    };
  }

  // File upload
  async uploadFile(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response: AxiosResponse<UploadResponse> = await this.api.post('/upload/single', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Multiple file upload
  async uploadMultipleFiles(files: File[]): Promise<UploadResponse[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response: AxiosResponse<UploadResponse[]> = await this.api.post('/upload/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.token;
  }

  getToken(): string | null {
    return this.token;
  }
}

export const apiService = new ApiService();

// React hook for API operations
export const useApiService = () => {
  return {
    apiService,
    isAuthenticated: apiService.isAuthenticated(),
    getToken: apiService.getToken(),
  };
};

// Utility functions for common operations
export const apiUtils = {
  // Format API errors for display
  formatError: (error: any): string => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    return error.message || 'An unexpected error occurred';
  },

  // Transform dates from API responses
  transformDates: <T extends Record<string, any>>(obj: T): T => {
    const transformed = { ...obj };
    const dateFields = ['createdAt', 'updatedAt', 'publishedAt', 'lastLoginAt', 'created_at', 'updated_at', 'last_login'];
    
    dateFields.forEach(field => {
      if (field in transformed && transformed[field]) {
        (transformed as any)[field] = new Date(transformed[field]);
      }
    });
    return transformed;
  },

  // Prepare data for API requests (convert dates to ISO strings)
  prepareForApi: <T extends Record<string, any>>(obj: T): T => {
    const prepared = { ...obj };
    Object.keys(prepared).forEach(key => {
      if (prepared[key] instanceof Date) {
        (prepared as any)[key] = prepared[key].toISOString();
      }
    });
    return prepared;
  },
};

export default apiService;